// Emergency mesh (PMU-273): the packet the app writes and signs with its device key, and reads.
// The native mesh (android-src/mesh) checks the signature, dedupes and relays it; byte for byte the
// same layout as MeshPacket.java:
//   ver u8 · type u8 · ttl u8 · flags u8 · origin 16 · to 16 (zeros: everyone) · created u64 ms · len u16 · payload · sig 64
// The TTL is left out of the signature (relays lower it); the id is the first 16 bytes of SHA-256
// of the signed bytes, so every device dedupes the same way.
import { b64url, fromB64url, signBytes, verifyBytes } from "@vavr/core";

export const MESH = { EMERGENCY: 1, RECORD: 2, MESSAGE: 3, ALERT: 4, VOICE: 5, ACK: 6, SYNC: 7 } as const;
export const MESH_FLAG = { URGENT: 1, DRILL: 2 } as const;
export const MESH_TTL = 12;
export const MESH_MAX_PAYLOAD = 4096;
const HEADER = 46, SIG = 64;

export interface MeshPacketData { type: number; flags: number; ttl: number; origin: string; to: string; created: number; payload: Uint8Array; sig: Uint8Array }

const enc = new TextEncoder(), dec = new TextDecoder();
function id16(s: string): Uint8Array { const out = new Uint8Array(16); out.set(enc.encode(s).slice(0, 16)); return out; }
function str16(b: Uint8Array): string { let n = 0; while (n < b.length && b[n] !== 0) n++; return dec.decode(b.slice(0, n)); }

/** The bytes the origin signs: everything but the signature, with the TTL as zero. */
export function meshSignedBytes(p: Omit<MeshPacketData, "sig" | "ttl">): Uint8Array {
  if (p.payload.length > MESH_MAX_PAYLOAD) throw new Error("payload too large");
  const b = new Uint8Array(HEADER + p.payload.length);
  const v = new DataView(b.buffer);
  b[0] = 1; b[1] = p.type; b[2] = 0; b[3] = p.flags;
  b.set(id16(p.origin), 4); b.set(id16(p.to), 20);
  v.setBigUint64(36, BigInt(p.created));
  v.setUint16(44, p.payload.length);
  b.set(p.payload, HEADER);
  return b;
}

/** A packet written and signed by this device, ready for the mesh. */
export async function buildMeshPacket(privateKey: CryptoKey, p: Omit<MeshPacketData, "sig" | "ttl"> & { ttl?: number }): Promise<Uint8Array> {
  const signed = meshSignedBytes(p);
  const sig = fromB64url(await signBytes(privateKey, signed));
  const out = new Uint8Array(signed.length + SIG);
  out.set(signed); out[2] = Math.min(p.ttl ?? MESH_TTL, MESH_TTL); out.set(sig, signed.length);
  return out;
}

export function decodeMeshPacket(b: Uint8Array): MeshPacketData | null {
  if (b.length < HEADER + SIG || b.length > HEADER + MESH_MAX_PAYLOAD + SIG || b[0] !== 1) return null;
  const v = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const len = v.getUint16(44);
  if (b.length !== HEADER + len + SIG) return null;
  return {
    type: b[1]!, ttl: b[2]!, flags: b[3]!, origin: str16(b.slice(4, 20)), to: str16(b.slice(20, 36)), created: Number(v.getBigUint64(36)),
    payload: b.slice(HEADER, HEADER + len), sig: b.slice(HEADER + len),
  };
}

/** The packet's id (hex, 16 bytes), the same as MeshPacket.id() on every device. */
export async function meshPacketId(p: MeshPacketData): Promise<string> {
  const h = new Uint8Array(await crypto.subtle.digest("SHA-256", meshSignedBytes(p) as BufferSource));
  return [...h.slice(0, 16)].map((x) => x.toString(16).padStart(2, "0")).join("");
}

/** Signed by the device it names (the native mesh checks this too; the app checks it again). */
export function verifyMeshPacket(p: MeshPacketData, publicKey: string): Promise<boolean> {
  return verifyBytes(publicKey, meshSignedBytes(p), b64url(p.sig));
}
